import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/supabase-context';

const SubmitContentDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSubmitClick = (path, state = {}) => {
    setIsOpen(false);
    navigate(path, { state });
  };

  const submissionOptions = [
    {
      title: 'Submit Business',
      description: isAuthenticated ? 'Add your business listing' : 'Add your business or suggest one',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5 21V7L13 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19 21V12L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: isAuthenticated ? '/submit-business' : '/suggest-listing',
      state: {}
    },
    {
      title: 'Submit Event',
      description: 'Share an IDD-friendly event',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/submit-event',
      state: {}
    },
    {
      title: 'Submit Resource',
      description: 'Share a helpful resource',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 7H7V16H10V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 7H14V12H17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/submit-resource',
      state: {}
    },
    {
      title: 'Submit Blog Post',
      description: 'Share your story or insights',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M12 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21H17C18.1046 21 19 20.1046 19 19V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 3C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5C19 5.53043 18.7893 6.03914 18.4142 6.41421C18.0391 6.78929 17.5304 7 17 7C16.4696 7 15.9609 6.78929 15.5858 6.41421C15.2107 6.03914 15 5.53043 15 5C15 4.46957 15.2107 3.96086 15.5858 3.58579C15.9609 3.21071 16.4696 3 17 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 11H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/submit-blog',
      state: {}
    }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-gray-700 hover:text-[#4BB0A8] px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={isAuthenticated ? "Add listing menu" : "Suggest content menu"}
      >
        <span>{isAuthenticated ? 'Add Listing' : 'Suggest'}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {isAuthenticated ? 'Add Listing' : 'Suggest Content'}
            </p>
          </div>

          {isAuthenticated ? (
            // Logged-in users see all options
            submissionOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleSubmitClick(option.path, option.state)}
                className="w-full px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-start space-x-3 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {option.icon}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-gray-900 hover:text-[#4BB0A8]">
                    {option.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </button>
            ))
          ) : (
            // Non-logged-in users see limited options with login prompt
            <>
              {/* Suggest Business - available to all */}
              <button
                onClick={() => handleSubmitClick('/suggest-listing', {})}
                className="w-full px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-start space-x-3 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
                    <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 21V7L13 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19 21V12L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-gray-900 hover:text-[#4BB0A8]">
                    Suggest a Business
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Recommend a business for our directory
                  </p>
                </div>
              </button>

              {/* Login prompt for other submissions */}
              <div className="px-4 py-3 border-t border-gray-100 mt-2">
                <p className="text-xs text-gray-600 mb-3">
                  Want to list your business, event, resource, or blog?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSubmitClick('/login', {})}
                    className="flex-1 px-3 py-2 text-xs font-medium text-[#4BB0A8] border border-[#4BB0A8] rounded-md hover:bg-[#4BB0A8] hover:text-white transition-colors duration-200"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => handleSubmitClick('/register', {})}
                    className="flex-1 px-3 py-2 text-xs font-medium bg-gradient-to-r from-[#4BB0A8] to-[#9256A0] text-white rounded-md hover:opacity-90 transition-opacity duration-200"
                  >
                    Create Account
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubmitContentDropdown;
